// SPDX-License-Identifier: Elastic-2.0
/**
 * ClimateControls — current temp readout + target slider + mode buttons.
 *
 * Per task spec § 3:
 *   - Current temp readout: large, with unit derived from `state.unit`
 *     (default `"°C"`).
 *   - Target temp Slider: 5°C–35°C, step 0.5 — emits
 *     `set_temperature` with `{ target_c }`.
 *   - Mode buttons row: heat / cool / auto / off — emits
 *     `set_hvac_mode` with `{ mode }`.
 *
 * Per F-300 the climate state shape is open (`Record<string, any>`); we
 * read defensively and fall back to sane defaults when a key is missing.
 */
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Slider } from "@/components/Slider";

import type { Device } from "@/api/gateway";

import styles from "../DeviceModal.module.css";

const MODES: readonly Mode[] = ["heat", "cool", "auto", "off"];
type Mode = "heat" | "cool" | "auto" | "off";

export interface ClimateControlsProps {
  device: Device;
  onAction: (
    action: string,
    params: Record<string, unknown>,
    optimisticStatePatch?: Record<string, unknown>,
  ) => void;
  disabled?: boolean;
}

export function ClimateControls({
  device,
  onAction,
  disabled,
}: ClimateControlsProps): JSX.Element {
  const { t } = useTranslation();
  const state = device.state ?? {};
  const currentTemp =
    typeof state.current_temperature === "number"
      ? state.current_temperature
      : typeof state.temperature === "number"
        ? state.temperature
        : null;
  const targetTemp =
    typeof state.target_temperature === "number"
      ? state.target_temperature
      : typeof state.target_c === "number"
        ? state.target_c
        : 21;
  const unit = typeof state.unit === "string" ? state.unit : "°C";
  const mode = typeof state.mode === "string" ? state.mode : "off";

  function handleTarget(value: number): void {
    onAction(
      "set_temperature",
      { target_c: value },
      { target_temperature: value, target_c: value },
    );
  }

  function handleMode(next: Mode): void {
    onAction("set_hvac_mode", { mode: next }, { mode: next });
  }

  return (
    <div className={styles.controlsStack} data-testid="climate-controls">
      <div className={styles.climateReadout} data-testid="climate-current">
        <span className={styles.climateValue}>
          {currentTemp === null ? "—" : currentTemp}
        </span>
        <span className={styles.climateUnit}>{unit}</span>
      </div>
      <Slider
        value={targetTemp}
        onChange={handleTarget}
        min={5}
        max={35}
        step={0.5}
        label={t("device_modal.controls.target_temp")}
        formatValue={(v) => `${v}${unit}`}
        disabled={disabled}
      />
      <div className={styles.modeRow} role="group" aria-label={t("device_modal.controls.mode")}>
        {MODES.map((m) => (
          <Button
            key={m}
            type="button"
            variant={m === mode ? "primary" : "secondary"}
            size="sm"
            onClick={() => handleMode(m)}
            disabled={disabled}
            aria-pressed={m === mode}
            data-testid={`climate-mode-${m}`}
          >
            {t(`device_modal.controls.modes.${m}`)}
          </Button>
        ))}
      </div>
    </div>
  );
}

export default ClimateControls;
